import { useEffect, useState } from "react";
import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";

import type { ExtendedTeam } from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import { Banner, BannerList } from "~/components/design-system/index.js";

export function EmailRedirectManage() {
  const [{ data: redirects }, refetchRedirects] =
    apiHooks.configuration.getEmailRedirects();

  const [sourceEmail, setSourceEmail] = useState("");
  const [message, setMessage] = useState("");
  const [teamSearchQuery, setTeamSearchQuery] = useState("");
  const [selectedTeam, setSelectedTeam] = useState<ExtendedTeam | null>(null);
  const [teamNames, setTeamNames] = useState<Record<string, string>>({});

  const [{ data: teamSearchResults }] = apiHooks.teams.search(
    teamSearchQuery,
    10,
    0,
  );

  useEffect(() => {
    if (!redirects) return;
    const unknownIds = redirects
      .map((r) => r.targetTeamId)
      .filter((id) => !(id in teamNames));
    if (unknownIds.length === 0) return;

    Promise.all(
      unknownIds.map((id) =>
        api.teams
          .get(id)
          .then((res) => [id, res.data?.name ?? id] as const)
          .catch(() => [id, id] as const),
      ),
    ).then((entries) => {
      setTeamNames((prev) => ({
        ...prev,
        ...Object.fromEntries(entries),
      }));
    });
  }, [redirects]);

  const isValid =
    sourceEmail.includes("@") && sourceEmail.trim().length > 3 && selectedTeam;

  return redirects ? (
    <Box>
      <Box sx={{ marginBottom: "1em" }}>
        <Banner
          title="Email redirects ensure that forms sent to old team email addresses are automatically routed to the specified target team, with a friendly message explaining the redirect."
          bannerType={BannerList.INFO}
          dense={true}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1em",
          marginBottom: "2em",
          padding: "1em",
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
        }}
      >
        <TextField
          label="Source email (the old address to redirect from)"
          value={sourceEmail}
          onChange={(e) => setSourceEmail(e.target.value)}
          fullWidth
          type="email"
        />

        <Autocomplete
          options={teamSearchResults?.results ?? []}
          getOptionLabel={(option) => `${option.name} (${option.primaryEmail})`}
          value={selectedTeam}
          onChange={(_e, value) => setSelectedTeam(value)}
          onInputChange={(_e, value) => setTeamSearchQuery(value)}
          renderInput={(params) => (
            <TextField {...params} label="Target team (redirect destination)" />
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />

        <TextField
          label="Message (shown to the form sender)"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          fullWidth
          multiline
          rows={2}
          placeholder="e.g. This team has been merged into Central MHL Team. Your form has been redirected."
        />

        <Button
          disabled={!isValid}
          variant="contained"
          onClick={async () => {
            if (!selectedTeam) return;
            await api.configuration.addEmailRedirect(
              sourceEmail,
              selectedTeam.id,
              message || undefined,
            );
            setSourceEmail("");
            setMessage("");
            setSelectedTeam(null);
            refetchRedirects();
          }}
          sx={{ alignSelf: "flex-start" }}
        >
          <Add sx={{ marginRight: "0.25em" }} /> Add Redirect
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="email redirects table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Source Email</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Target Team</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Message</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Created</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {redirects.map((redirect) => (
              <TableRow
                key={redirect.id}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell>{redirect.sourceEmail}</TableCell>
                <TableCell>
                  {teamNames[redirect.targetTeamId] ?? redirect.targetTeamId}
                </TableCell>
                <TableCell>{redirect.message ?? "—"}</TableCell>
                <TableCell>
                  {new Date(redirect.createdAt).toLocaleDateString("en-GB")}
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={async () => {
                      await api.configuration.removeEmailRedirect(redirect.id);
                      refetchRedirects();
                    }}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {redirects.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No email redirects configured
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  ) : (
    <>Loading...</>
  );
}
